import isEqual from 'lodash/isEqual';
import { Delta } from 'quill/core';
import DiffAccordion from '../formats/DiffAccordion';
import createQuillInstance from './createQuillInstance';
var DiffStatus;
(function (DiffStatus) {
    DiffStatus[DiffStatus["INSERTED"] = 1] = "INSERTED";
    DiffStatus[DiffStatus["DELETED"] = 2] = "DELETED";
    DiffStatus[DiffStatus["RETAINED"] = 3] = "RETAINED";
})(DiffStatus || (DiffStatus = {}));
const DEFAULT_DIFF_CONTEXT_LINES = 2;
function isChangedDiffStatus(op) {
    const diffStatus = op.attributes?.diffStatus;
    return diffStatus === DiffStatus.INSERTED || diffStatus === DiffStatus.DELETED;
}
function appendOpToDelta(delta, op) {
    if (op.insert !== undefined) {
        delta.insert(op.insert, op.attributes);
    }
    else if (op.retain) {
        delta.retain(op.retain, op.attributes);
    }
    else if (op.delete) {
        delta.delete(op.delete);
    }
}
function splitDeltaIntoLines(delta) {
    const lines = [{ ops: [], hasChanges: false }];
    const finalizeLine = () => {
        lines.push({ ops: [], hasChanges: false });
    };
    for (let i = 0; i < delta.ops.length; i++) {
        const op = delta.ops[i];
        const isChanged = isChangedDiffStatus(op);
        lines[lines.length - 1].hasChanges =
            lines[lines.length - 1].hasChanges || isChanged;
        if (typeof op.insert === 'string') {
            const parts = op.insert.split('\n');
            for (let j = 0; j < parts.length; j++) {
                const part = parts[j];
                const activeLine = lines[lines.length - 1];
                if (part) {
                    activeLine.ops.push({
                        insert: part,
                        attributes: op.attributes,
                    });
                }
                if (j < parts.length - 1) {
                    activeLine.ops.push({
                        insert: '\n',
                        attributes: op.attributes,
                    });
                    finalizeLine();
                    lines[lines.length - 1].hasChanges =
                        lines[lines.length - 1].hasChanges || isChanged;
                }
            }
            continue;
        }
        lines[lines.length - 1].ops.push(op);
    }
    const lastLine = lines[lines.length - 1];
    if (lastLine.ops.length === 0 && lines.length > 1) {
        lines.pop();
    }
    return lines;
}
function getMergedRanges(changedLineIndexes, totalLines, contextLines) {
    if (!changedLineIndexes.length) {
        return [];
    }
    const ranges = changedLineIndexes.map(lineIndex => ({
        start: Math.max(0, lineIndex - contextLines),
        end: Math.min(totalLines - 1, lineIndex + contextLines),
    }));
    const mergedRanges = [ranges[0]];
    for (let i = 1; i < ranges.length; i++) {
        const currentRange = ranges[i];
        const prevRange = mergedRanges[mergedRanges.length - 1];
        if (currentRange.start <= prevRange.end + 1) {
            prevRange.end = Math.max(prevRange.end, currentRange.end);
        }
        else {
            mergedRanges.push(currentRange);
        }
    }
    return mergedRanges;
}
function getHiddenLinesLabel(hiddenLines) {
    return hiddenLines === 1 ? 'Show 1 line' : `Show ${hiddenLines} lines`;
}
function cloneOp(op) {
    return {
        ...op,
        attributes: op.attributes ? { ...op.attributes } : undefined,
    };
}
function getHiddenRangeOps(lines, startLine, endLine) {
    const hiddenOps = [];
    for (let lineIndex = startLine; lineIndex <= endLine; lineIndex++) {
        const line = lines[lineIndex];
        for (let opIndex = 0; opIndex < line.ops.length; opIndex++) {
            hiddenOps.push(cloneOp(line.ops[opIndex]));
        }
    }
    return hiddenOps;
}
function insertCollapsedLinesMarker(delta, theme, lines, hiddenLines, startLine, endLine) {
    if (hiddenLines <= 0) {
        return;
    }
    const hiddenLabel = getHiddenLinesLabel(hiddenLines);
    const value = {
        title: hiddenLabel,
        body: { ops: getHiddenRangeOps(lines, startLine, endLine) },
    };
    delta.insert({ [DiffAccordion.blotName]: value });
}
function getCollapsedDiffViewDelta(theme, diffViewDelta, contextLines = DEFAULT_DIFF_CONTEXT_LINES) {
    const lines = splitDeltaIntoLines(diffViewDelta);
    const changedLineIndexes = [];
    for (let i = 0; i < lines.length; i++) {
        if (lines[i].hasChanges) {
            changedLineIndexes.push(i);
        }
    }
    const mergedRanges = getMergedRanges(changedLineIndexes, lines.length, contextLines);
    const collapsedDelta = new Delta();
    const hasLeadingGap = mergedRanges.length > 0 && mergedRanges[0].start > 0;
    if (hasLeadingGap) {
        const hiddenLeadingLines = mergedRanges[0].start;
        insertCollapsedLinesMarker(collapsedDelta, theme, lines, hiddenLeadingLines, 0, mergedRanges[0].start - 1);
    }
    for (let i = 0; i < mergedRanges.length; i++) {
        const range = mergedRanges[i];
        for (let lineIndex = range.start; lineIndex <= range.end; lineIndex++) {
            const line = lines[lineIndex];
            for (let opIndex = 0; opIndex < line.ops.length; opIndex++) {
                appendOpToDelta(collapsedDelta, line.ops[opIndex]);
            }
        }
        const hasMoreHunks = i < mergedRanges.length - 1;
        if (hasMoreHunks) {
            const nextRange = mergedRanges[i + 1];
            const hiddenLinesBetween = nextRange.start - range.end - 1;
            insertCollapsedLinesMarker(collapsedDelta, theme, lines, hiddenLinesBetween, range.end + 1, nextRange.start - 1);
        }
    }
    const hasTrailingGap = mergedRanges.length > 0 &&
        mergedRanges[mergedRanges.length - 1].end < lines.length - 1;
    if (hasTrailingGap) {
        const hiddenTrailingLines = lines.length - mergedRanges[mergedRanges.length - 1].end - 1;
        insertCollapsedLinesMarker(collapsedDelta, theme, lines, hiddenTrailingLines, mergedRanges[mergedRanges.length - 1].end + 1, lines.length - 1);
    }
    return collapsedDelta;
}
const EMBED_PLACEHOLDER = '\uFFFC';
const LINE_CHANGE_WORD_THRESHOLD = 0.5;
function deltaToPlainText(delta) {
    let text = '';
    for (const op of delta.ops) {
        if (typeof op.insert === 'string') {
            text += op.insert;
        }
        else if (op.insert) {
            text += EMBED_PLACEHOLDER;
        }
    }
    return text;
}
function expandToWordBounds(text, changed) {
    let i = 0;
    while (i < text.length) {
        if (changed[i]) {
            const runStart = i;
            while (i < text.length && changed[i])
                i++;
            const runEnd = i;
            let start = runStart;
            while (start > 0 && text[start - 1] !== '\n' && /\S/.test(text[start - 1])) {
                changed[--start] = true;
            }
            let end = runEnd;
            while (end < text.length && text[end] !== '\n' && /\S/.test(text[end])) {
                changed[end++] = true;
            }
            i = end;
        }
        else {
            i++;
        }
    }
}
function applyLineThreshold(text, changed) {
    let lineStart = 0;
    for (let i = 0; i <= text.length; i++) {
        if (i === text.length || text[i] === '\n') {
            const lineEnd = i;
            let totalWords = 0;
            let changedWords = 0;
            let j = lineStart;
            while (j < lineEnd) {
                while (j < lineEnd && /\s/.test(text[j]))
                    j++;
                if (j >= lineEnd)
                    break;
                const wordStart = j;
                while (j < lineEnd && /\S/.test(text[j]))
                    j++;
                totalWords++;
                let hasChange = false;
                for (let k = wordStart; k < j; k++) {
                    if (changed[k]) {
                        hasChange = true;
                        break;
                    }
                }
                if (hasChange)
                    changedWords++;
            }
            if (totalWords > 0 && changedWords / totalWords > LINE_CHANGE_WORD_THRESHOLD) {
                for (let k = lineStart; k < lineEnd; k++) {
                    changed[k] = true;
                }
            }
            lineStart = i + 1;
        }
    }
}
function computeWordLevelDiff(oldDelta, newDelta, charDiffDelta) {
    const oldText = deltaToPlainText(oldDelta);
    const newText = deltaToPlainText(newDelta);
    const oldChanged = new Array(oldText.length).fill(false);
    const newChanged = new Array(newText.length).fill(false);
    let oldPos = 0;
    let newPos = 0;
    for (const op of charDiffDelta.ops) {
        if (op.retain != null) {
            if (typeof op.retain === 'number') {
                oldPos += op.retain;
                newPos += op.retain;
            }
            else {
                oldPos += 1;
                newPos += 1;
            }
        }
        else if (op.delete) {
            for (let i = 0; i < op.delete; i++)
                oldChanged[oldPos + i] = true;
            oldPos += op.delete;
        }
        else if (op.insert) {
            const len = typeof op.insert === 'string' ? op.insert.length : 1;
            for (let i = 0; i < len; i++)
                newChanged[newPos + i] = true;
            newPos += len;
        }
    }
    expandToWordBounds(oldText, oldChanged);
    expandToWordBounds(newText, newChanged);
    applyLineThreshold(oldText, oldChanged);
    applyLineThreshold(newText, newChanged);
    const result = new Delta();
    oldPos = 0;
    newPos = 0;
    for (const op of charDiffDelta.ops) {
        if (op.retain != null) {
            if (typeof op.retain === 'number') {
                let i = 0;
                while (i < op.retain) {
                    const isChanged = oldChanged[oldPos + i] || newChanged[newPos + i];
                    let runLen = 0;
                    while (i + runLen < op.retain &&
                        (oldChanged[oldPos + i + runLen] || newChanged[newPos + i + runLen]) === isChanged) {
                        runLen++;
                    }
                    if (isChanged) {
                        result.delete(runLen);
                        const sliced = newDelta.slice(newPos + i, newPos + i + runLen);
                        for (const slicedOp of sliced.ops) {
                            if (slicedOp.insert !== undefined) {
                                result.insert(slicedOp.insert, slicedOp.attributes);
                            }
                        }
                    }
                    else {
                        result.retain(runLen, op.attributes);
                    }
                    i += runLen;
                }
                oldPos += op.retain;
                newPos += op.retain;
            }
            else {
                result.retain(op.retain, op.attributes);
                oldPos += 1;
                newPos += 1;
            }
        }
        else if (op.delete) {
            result.delete(op.delete);
            oldPos += op.delete;
        }
        else if (op.insert) {
            const len = typeof op.insert === 'string' ? op.insert.length : 1;
            const sliced = newDelta.slice(newPos, newPos + len);
            for (const slicedOp of sliced.ops) {
                if (slicedOp.insert !== undefined) {
                    result.insert(slicedOp.insert, slicedOp.attributes);
                }
            }
            newPos += len;
        }
    }
    return result;
}
function flattenDelta(delta) {
    const chars = [];
    for (const op of delta.ops) {
        if (typeof op.insert === 'string') {
            for (const ch of op.insert) {
                chars.push({ value: ch, attributes: op.attributes });
            }
        }
        else if (op.insert) {
            chars.push({ value: op.insert, attributes: op.attributes });
        }
    }
    return chars;
}
function buildDiffViewDelta(oldDelta, diffDelta) {
    const oldChars = flattenDelta(oldDelta);
    const result = new Delta();
    let oldPos = 0;
    for (const op of diffDelta.ops) {
        if (op.retain != null) {
            const len = typeof op.retain === 'number' ? op.retain : 1;
            for (let i = 0; i < len; i++) {
                const entry = oldChars[oldPos + i];
                result.insert(entry.value, {
                    ...entry.attributes,
                    ...op.attributes,
                    diffStatus: DiffStatus.RETAINED,
                });
            }
            oldPos += len;
        }
        else if (op.delete) {
            for (let i = 0; i < op.delete; i++) {
                const entry = oldChars[oldPos + i];
                result.insert(entry.value, {
                    ...entry.attributes,
                    diffStatus: DiffStatus.DELETED,
                });
            }
            oldPos += op.delete;
        }
        else if (op.insert) {
            result.insert(op.insert, {
                ...op.attributes,
                diffStatus: DiffStatus.INSERTED,
            });
        }
    }
    return result;
}
function updateDiffStyles(theme, delta) {
    for (let i = 0; i < delta.ops.length; ++i) {
        const op = delta.ops[i];
        if (op.attributes?.diffStatus) {
            if (op.attributes.diffStatus === DiffStatus.INSERTED) {
                if (op.insert && typeof op.insert !== 'string') {
                    op.attributes.padding = `${theme.spacing.standard.md}px`;
                    op.attributes.display = 'block';
                }
                op.attributes.background = theme.palette.background.positive;
            }
            else if (op.attributes.diffStatus === DiffStatus.DELETED) {
                op.attributes.background = theme.palette.background.negative;
                op.attributes.strike = true;
            }
        }
    }
    return delta;
}
export function getDiffViewDelta(theme, quill, oldHTML, newHTML, options = {}) {
    const oldDelta = quill.clipboard.convert({ html: oldHTML });
    const newDelta = quill.clipboard.convert({ html: newHTML });
    const charDiffDelta = oldDelta.diff(newDelta);
    const wordDiffDelta = computeWordLevelDiff(oldDelta, newDelta, charDiffDelta);
    const diffViewDelta = updateDiffStyles(theme, buildDiffViewDelta(oldDelta, wordDiffDelta));
    if (!options.showOnlyDiff) {
        return diffViewDelta;
    }
    const collapsedDelta = getCollapsedDiffViewDelta(theme, diffViewDelta, options.contextLines ?? DEFAULT_DIFF_CONTEXT_LINES);
    return collapsedDelta.ops.length ? collapsedDelta : diffViewDelta;
}
export function getDiffDelta(oldHTML, newHTML, quill = createQuillInstance(document.createElement('div'), { readOnly: false })) {
    const oldDelta = typeof oldHTML === 'string' ? quill.clipboard.convert({ html: oldHTML }) : new Delta(oldHTML);
    const newDelta = typeof newHTML === 'string' ? quill.clipboard.convert({ html: newHTML }) : new Delta(newHTML);
    return oldDelta.diff(newDelta);
}
export function doesDeltaContainInsOrDel(delta) {
    return delta.ops.some(op => {
        return op.insert || op.delete;
    });
}
export function areEqualDeltas(a, b) {
    const aOps = typeof a === 'object' ? a.ops : a;
    const bOps = typeof b === 'object' ? b.ops : b;
    return aOps.every((op, index) => {
        const bOp = bOps[index];
        return op.insert === bOp.insert && op.delete === bOp.delete && op.retain === bOp.retain && isEqual(op.attributes, bOp.attributes);
    });
}
